/**
 * Projetos de correias — o outro tipo de conteúdo do Sistema.
 *
 * Não é pinout nem diagrama: é a correia de sincronismo, e a estrutura é
 * própria. Um projeto de correia tem **arquivos** (cada um é uma prancha, com
 * as imagens do trajeto), **modelos** (com veículo, motor e faixa de ano) e os
 * **produtos** em que aparece.
 *
 * **As imagens não passam por aqui.** O registro guarda o *caminho no servidor
 * antigo* — `C:\inetpub\diagweb.com.br\sistema\Img\correias319.jpg` — e a API
 * não serve arquivo de imagem. É a mesma parede do cache do pinout gráfico: o
 * arquivo mora no IIS antigo, que o host da API não enxerga. A tela mostra
 * quantas imagens há e em que ordem, e diz onde elas estão.
 *
 * Por isso este módulo **não tem escrita de imagem** — só a ordenação, que é
 * sobre os registros e não sobre os arquivos.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { api, foraDoProjeto, obter } from "./cliente"

export type ProjetoDeCorreias = {
  id: number
  descricao: string
  atualizar: boolean
  dataAtualizacao: string | null
}

export type ArquivoDeCorreias = {
  id: number
  projetoCorreiasId: number
  descricao: string
  modeloIds: number[]
  sistemaIds: number[]
}

export type ImagemDeCorreias = {
  id: number
  arquivoCorreiasId: number
  /** O caminho no servidor antigo. Não é URL: a API não serve o arquivo. */
  descricao: string
  ordem: number
}

export type ModeloDeCorreias = {
  id: number
  projetoCorreiasId: number
  descricao: string
  veiculoId: number
  veiculoModelo: string
  motorId: number
  motorNome: string
  anoInicial: string | null
  anoFinal: string | null
  publicado?: boolean
}

export type ProdutoDeCorreias = {
  produtoId: number
  produtoNome: string
  publicado: boolean
  publicadoEm: string | null
}

/**
 * Os projetos de correias.
 *
 * **A rota devolve página, as filhas devolvem array.** `api/projetos-correias`
 * responde `{ totalItens, paginaAtual, pageSize, itens }`; arquivos, modelos e
 * produtos respondem a lista crua. A tela lia as duas como array e quebrava
 * inteira — `lista.map is not a function`, nada na tela, e o erro derrubava a
 * rota seguinte junto.
 *
 * Aqui o hook aceita as duas formas e devolve sempre a lista, como o
 * `usarMestre` dos outros projetos já fazia com o mesmo problema.
 */
export function usarProjetosDeCorreias(params: { busca?: string; ordem?: string; pagina?: number; pageSize?: number } = {}) {
  // **A rota pagina.** Sem parâmetro ela devolvia os 50 mais novos, e a tela lia isso como
  // a lista inteira: projeto além do 50º não aparecia, nem buscando.
  const q = useQuery({
    queryKey: ["correias", params] as const,
    queryFn: () => obter<PaginaDeCorreias | ProjetoDeCorreias[]>("api/projetos-correias", params),
  })
  const bruto = q.data
  const itens = Array.isArray(bruto) ? bruto : (bruto?.itens ?? [])
  const total = Array.isArray(bruto) ? bruto.length : (bruto?.totalItens ?? itens.length)
  return { ...q, data: itens, total }
}

type PaginaDeCorreias = { itens?: ProjetoDeCorreias[]; totalItens?: number }

export function usarArquivosDeCorreias(projetoId: number) {
  return useQuery({
    queryKey: ["correias-arquivos", projetoId] as const,
    enabled: projetoId > 0,
    queryFn: () => obter<ArquivoDeCorreias[]>(`api/projetos-correias/${projetoId}/arquivos`, {}),
  })
}

/**
 * Reordena as imagens de um arquivo de correia.
 *
 * **Só mexe no metadado.** A ordem é uma coluna; o arquivo continua onde está,
 * no servidor antigo. É por isso que isto funciona hoje, enquanto subir imagem
 * não — a escrita aqui não passa pelo servidor de arquivos.
 *
 * O corpo é a lista de ids **na ordem desejada**; quem numera é a API.
 */
export function usarOrdenarImagensDeCorreias(arquivoId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (ids: number[]) => {
      await api.put(`api/projetos-correias/arquivos/${arquivoId}/imagens/ordem`, { ids },
        foraDoProjeto())
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["correias-imagens", arquivoId] }),
  })
}

export function usarImagensDeCorreias(arquivoId: number) {
  return useQuery({
    queryKey: ["correias-imagens", arquivoId] as const,
    enabled: arquivoId > 0,
    queryFn: () => obter<ImagemDeCorreias[]>(
      `api/projetos-correias/arquivos/${arquivoId}/imagens`, {}),
  })
}

export function usarModelosDeCorreias(projetoId: number) {
  return useQuery({
    queryKey: ["correias-modelos", projetoId] as const,
    enabled: projetoId > 0,
    queryFn: () => obter<ModeloDeCorreias[]>(`api/projetos-correias/${projetoId}/modelos`, {}),
  })
}

/**
 * Põe no ar e tira do ar um modelo de correias.
 *
 * A publicação é **por modelo**, e não do projeto: cada veículo/motor do projeto
 * aparece para quem paga quando o seu modelo está publicado. Despublicar não apaga.
 */
export function usarPublicarModeloDeCorreias(projetoId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, publicar }: { id: number; publicar: boolean }) => {
      await api.post(`api/projetos-correias/${projetoId}/modelos/${id}/${publicar ? "publicar" : "despublicar"}`,
                     null, foraDoProjeto())
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["correias-modelos", projetoId] }),
  })
}

export function usarProdutosDeCorreias(projetoId: number) {
  return useQuery({
    queryKey: ["correias-produtos", projetoId] as const,
    enabled: projetoId > 0,
    queryFn: () => obter<ProdutoDeCorreias[]>(`api/projetos-correias/${projetoId}/produtos`, {}),
  })
}

/**
 * Correias não pertencem a projeto de pinout, então a trava do canário não tem
 * o que conferir — é `foraDoProjeto`, como o login e os catálogos globais.
 *
 * Vale registrar o que isso quer dizer: **em desenvolvimento, escrever aqui
 * escreve em produção**. A trava protege o módulo de pinout porque lá existe um
 * projeto canário; aqui não existe equivalente. Por isso a tela avisa antes de
 * qualquer gravação, em vez de deixar a pessoa descobrir depois.
 */
export function usarSalvarProjetoDeCorreias() {
  const qc = useQueryClient()
  return useMutation({
    // **Devolve o projeto criado**, e não só grava. A tela abre o registro logo
    // depois de criar, porque projeto de correias sem arquivo nasce inútil — e
    // sem o id de volta ela teria de procurar o que acabou de fazer numa lista.
    mutationFn: async ({ id, descricao }: { id: number | null; descricao: string }) => {
      if (id) {
        await api.put(`api/projetos-correias/${id}`, { descricao }, foraDoProjeto())
        return null
      }
      const r = await api.post<ProjetoDeCorreias>("api/projetos-correias", { descricao },
                                                  foraDoProjeto())
      return r.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["correias"] }),
  })
}

export function usarSalvarArquivoDeCorreias(projetoId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, descricao }: { id: number | null; descricao: string }) => {
      if (id) {
        await api.put(`api/projetos-correias/${projetoId}/arquivos/${id}`, { descricao }, foraDoProjeto())
      } else {
        await api.post(`api/projetos-correias/${projetoId}/arquivos`, { descricao }, foraDoProjeto())
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["correias-arquivos", projetoId] }),
  })
}

export function usarExcluirArquivoDeCorreias(projetoId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`api/projetos-correias/${projetoId}/arquivos/${id}`, foraDoProjeto())
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["correias-arquivos", projetoId] }),
  })
}
